import { Navigate, Outlet, createFileRoute } from "@tanstack/react-router";
import { useConvexAuth } from "convex/react";
import { NightAppShell } from "@/shared/layout/NightAppShell";
import { Skeleton } from "@/shared/ui/Skeleton";

/**
 * Pathless layout protecting `/`, `/leagues/*`, `/settings`. Auth state is
 * cookie-backed Better Auth, async-loaded into Convex's reactive client.
 *
 * We use `<Navigate>` (not `throw redirect()`) because the auth state isn't
 * known synchronously at navigation time — it resolves over a Convex query.
 * `throw redirect()` from inside a component render bypasses the router's
 * redirect handling and surfaces in the React error boundary as a generic
 * `Response { status: 307 }`.
 */
export const Route = createFileRoute("/_authenticated")({
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const { isLoading, isAuthenticated } = useConvexAuth();

  if (isLoading) {
    return (
      <NightAppShell>
        <div className="flex flex-col gap-3 pt-2">
          <Skeleton className="h-8 w-1/3" />
          <Skeleton className="h-24 w-full" />
        </div>
      </NightAppShell>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth/sign-in" />;
  }

  return (
    <NightAppShell>
      <Outlet />
    </NightAppShell>
  );
}
